// AstriaVietnamV2Shell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Heart, Hourglass, CloudSun, Compass, NotebookPen } from "lucide-react";
import { VietnamV2Lang } from "./vietnamV2Language";
import { VIETNAM_V2_COPY } from "./vietnamV2Copy";

// ─── Astria Vietnam v2 — shared shell (header + sticky tab bar) ───────────
// Per client spec (Vn.txt — VN v2 Full Deployment JSON Pack): soft-warm,
// mềm/dịu/ấm/yên, gentle-clarity, non-predictive. A jade / lotus-pink /
// warm-cream palette — distinct from PH v2's coral/rose-gold and ID v2's
// amber/teal, but same structural pattern (see AstriaPhilippinesV2Shell.tsx
// / AstriaIndonesiaV2Shell.tsx). 6 fixed tabs, no birth chart, no LLM — see
// helper/astriaVietnamV2Service.js on the backend.
//
// Bilingual (EN/VI) — a self-contained toggle in the header switches all UI
// chrome text via vietnamV2Copy.ts. Scoped to this feature only; does not
// touch the app-wide i18next setup. See vietnamV2Language.ts.

export const VIETNAM_V2_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const VIETNAM_V2_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const VIETNAM_V2_MAX_WIDTH = 960;

export const SHELL_COLORS_VIETNAM_V2 = {
  bg: "linear-gradient(160deg, #FAFDFB 0%, #EFF9F3 55%, #FFF6F5 100%)",
  card: "#FFFFFF",
  section: "#EAF7EF",
  cardBorder: "#CDE9D8",
  titleColor: "#233B30",
  sectionTitleColor: "#233B30",
  bodyColor: "#3D584A",
  mutedColor: "#84A695",
  accent: "#3C9169",
  accentSoft: "rgba(60,145,105,0.14)",
  accentGlow: "0 0 0 3px rgba(60,145,105,0.18)",
  gold: "#E88C9A",
  goldSoft: "rgba(232,140,154,0.14)",
  rose: "#F2B9C4",
  white: "#FFFFFF",
};

export type AstriaVietnamV2Tab =
  | "soft_summary"
  | "connection_atmosphere"
  | "timing_rhythm"
  | "emotion_balance"
  | "guidance_path"
  | "your_note";

interface TabDef {
  key: AstriaVietnamV2Tab;
  // The SubCategory name exactly as seeded by
  // scripts/createAstriaVietnamV2Category.js — always English, used to
  // resolve categoryId/subCategoryId in useVietnamV2Tab.ts regardless of
  // the active display language. Never render this directly; use the
  // language-keyed copy.tabBar[key].label for display.
  dbName: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

// Icons/order per Vn.txt's VN v2 UI pack: 🌙 💞 ⏳ 🌤️ 🧭 📝
const TABS: TabDef[] = [
  {
    key: "soft_summary",
    dbName: "Soft Summary",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-vietnam-v2/soft-summary",
    accent: ["#3C9169", "#F2B9C4"],
  },
  {
    key: "connection_atmosphere",
    dbName: "Connection Atmosphere",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-vietnam-v2/connection-atmosphere",
    accent: ["#E88C9A", "#F2B9C4"],
  },
  {
    key: "timing_rhythm",
    dbName: "Timing Rhythm",
    icon: <Hourglass className="w-4 h-4" />,
    path: "/astria-vietnam-v2/timing-rhythm",
    accent: ["#3C9169", "#E88C9A"],
  },
  {
    key: "emotion_balance",
    dbName: "Emotion Balance",
    icon: <CloudSun className="w-4 h-4" />,
    path: "/astria-vietnam-v2/emotion-balance",
    accent: ["#F2B9C4", "#3C9169"],
  },
  {
    key: "guidance_path",
    dbName: "Guidance Path",
    icon: <Compass className="w-4 h-4" />,
    path: "/astria-vietnam-v2/guidance-path",
    accent: ["#3C9169", "#E88C9A"],
  },
  {
    key: "your_note",
    dbName: "Your Note",
    icon: <NotebookPen className="w-4 h-4" />,
    path: "/astria-vietnam-v2/your-note",
    accent: ["#E88C9A", "#3C9169"],
  },
];

interface AstriaVietnamV2ShellProps {
  active: AstriaVietnamV2Tab;
  lang: VietnamV2Lang;
  onLangChange: (lang: VietnamV2Lang) => void;
  children: React.ReactNode;
}

const AstriaVietnamV2Shell: React.FC<AstriaVietnamV2ShellProps> = ({
  active,
  lang,
  onLangChange,
  children,
}) => {
  const navigate = useNavigate();
  const copy = VIETNAM_V2_COPY[lang];

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_VIETNAM_V2.bg,
        fontFamily: VIETNAM_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(250,253,251,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_VIETNAM_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: VIETNAM_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_VIETNAM_V2.accent} 0%, ${SHELL_COLORS_VIETNAM_V2.gold} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${SHELL_COLORS_VIETNAM_V2.accentSoft}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, color: "#fff" }}>💞</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: SHELL_COLORS_VIETNAM_V2.titleColor,
                fontFamily: VIETNAM_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              {copy.shell.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: SHELL_COLORS_VIETNAM_V2.mutedColor,
              }}
            >
              {copy.shell.subtitle}
            </div>
          </div>

          {/* ─── EN / VI language toggle ─────────────────────────────── */}
          <div
            role="group"
            aria-label={copy.shell.langToggleLabel}
            style={{
              display: "flex",
              flexShrink: 0,
              borderRadius: 999,
              background: SHELL_COLORS_VIETNAM_V2.section,
              border: `1px solid ${SHELL_COLORS_VIETNAM_V2.cardBorder}`,
              padding: 3,
              gap: 2,
            }}
          >
            {(["vi", "en"] as VietnamV2Lang[]).map((code) => {
              const isActive = code === lang;
              return (
                <button
                  key={code}
                  onClick={() => onLangChange(code)}
                  aria-pressed={isActive}
                  className="rounded-full transition-all"
                  style={{
                    paddingInline: 10,
                    paddingBlock: 5,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.03em",
                    color: isActive ? "#FFFFFF" : SHELL_COLORS_VIETNAM_V2.mutedColor,
                    background: isActive
                      ? `linear-gradient(135deg, ${SHELL_COLORS_VIETNAM_V2.accent} 0%, ${SHELL_COLORS_VIETNAM_V2.gold} 100%)`
                      : "transparent",
                  }}
                >
                  {code.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: VIETNAM_V2_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: isActive
                      ? "#FFFFFF"
                      : SHELL_COLORS_VIETNAM_V2.bodyColor,
                    background: isActive
                      ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                      : SHELL_COLORS_VIETNAM_V2.section,
                    boxShadow: isActive
                      ? `0 4px 14px ${SHELL_COLORS_VIETNAM_V2.accentSoft}`
                      : "none",
                    border: isActive
                      ? "none"
                      : `1px solid ${SHELL_COLORS_VIETNAM_V2.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{copy.tabBar[tab.key].label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background:
                "linear-gradient(90deg, rgba(250,253,251,0) 0%, rgba(250,253,251,0.9) 100%)",
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: VIETNAM_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaVietnamV2Shell;
export { TABS as ASTRIA_VIETNAM_V2_TABS };
